// @ts-nocheck
/**
 * Single Product Customizer V2 - Minimal Implementation
 * Extends BaseCustomizer with only single-product specific logic
 */

class SingleProductCustomizer extends BaseCustomizer {
  constructor(customizerElement) {
    super({
      selectors: {
        customizer: '[data-single-customizer]',
        form: 'form[action="/cart/add"]',
        addToCartButton: '[name="add"]',
        variantInput: '[name="id"]',
        quantityInput: '[name="quantity"]',
        priceContainer: '.price'
      },
      autoInit: false
    });

    this.customizerElement = customizerElement;
    this.productId = null;
    this.sectionId = null;
    this.product = null;
    this.variants = [];
    this.currentVariant = null;
    this.extraVariants = new Map();
    this.storageKey = null;

    this.initializeSingle();
  }

  initializeSingle() {
    this.customizer = this.customizerElement || document.querySelector(this.config.selectors.customizer);
    if (!this.customizer) return;

    this.productId = this.customizer.dataset.productId;
    this.sectionId = this.customizer.dataset.sectionId;
    this.product = JSON.parse(this.customizer.dataset.product || '{}');
    this.variants = JSON.parse(this.customizer.dataset.variants || '[]');
    this.storageKey = `single-customizer-extra-variant-${this.productId}`;

    // Extract option names from dropdown headers for cart properties
    this.optionNames = [];
    const dropdownToggles = this.customizer.querySelectorAll('[data-dropdown-toggle][data-option-name]');
    dropdownToggles.forEach(toggle => {
      const optionName = toggle.dataset.optionName;
      if (optionName && optionName !== 'extra-variant') {
        this.optionNames.push(optionName);
      }
    });

    if (this.initialize()) {
      this.currentVariant = this.findInitialVariant();
      this.initializeState();
      this.updateFormVariantId(this.currentVariant?.id);
      this.customizer._customizerInstance = this;

      // Re-apply price update after theme variant updates (fix for sticky-add-to-cart)
      document.addEventListener('variant:update', (event) => {
        if (event.detail.data?.productId && this.productId && event.detail.data.productId.toString() === this.productId.toString()) {
          setTimeout(() => this.updatePrice(), 50);
        }
      });
    }
  }

  findElements() {
    this.productForm = document.querySelector(`#product-form-${this.sectionId}`) ||
      document.querySelector('form[action="/cart/add"]');
    if (!this.productForm) return false;

    this.addToCartButton = this.productForm.querySelector('[name="add"]') ||
      document.querySelector('[name="add"]');
    this.variantIdInput = this.productForm.querySelector('[name="id"]');
    this.quantityInput = this.productForm.querySelector('[name="quantity"]');

    // More specific price container targeting to avoid conflicts with theme price
    this.priceContainer = document.getElementById(`price-${this.sectionId}`) ||
      document.querySelector('product-price [ref="priceContainer"]') ||
      document.querySelector('.price__container') ||
      document.querySelector('.price');

    // Sticky price container
    this.stickyPriceContainer = document.querySelector('.sticky-add-to-cart__price') ||
      document.querySelector('[data-testid="sticky-price-display"]');

    return !!(this.addToCartButton && this.variantIdInput);
  }

  findInitialVariant() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');

    if (variantId && this.variants) {
      const matchingVariant = this.variants.find(v => v.id.toString() === variantId);
      if (matchingVariant) return matchingVariant;
    }

    return this.variants && this.variants.length > 0 ? this.variants[0] : null;
  }

  initializeState() {
    // Open first accordion
    const firstDropdown = this.customizer.querySelector('[data-variant-dropdown]');
    if (firstDropdown) {
      const header = firstDropdown.querySelector('[data-dropdown-toggle]');
      const content = firstDropdown.querySelector('[data-dropdown-content]');
      if (header && content) {
        header.classList.add(this.config.classes.active);
        content.classList.add(this.config.classes.expanded);
        this.currentOpenDropdown = firstDropdown;
      }
    }

    // Load saved extra variant
    const savedExtraVariantId = this.getStorageItem(this.storageKey);
    if (savedExtraVariantId) {
      const targetRadio = this.customizer.querySelector(`.extra-variant-radio[value="${savedExtraVariantId}"]`);
      if (targetRadio) {
        const defaultCheckedRadio = this.customizer.querySelector('.extra-variant-radio:checked');
        if (defaultCheckedRadio) defaultCheckedRadio.checked = false;
        targetRadio.checked = true;
        this.updateExtraVariantSelection(targetRadio);
      }
    }

    // Sync with current variant
    if (this.currentVariant) {
      this.syncAccordionsWithCurrentVariant();
    }

    // Initialize extra variant selection
    const checkedExtraRadio = this.customizer.querySelector('.extra-variant-radio:checked');
    if (checkedExtraRadio) {
      this.updateExtraVariantSelection(checkedExtraRadio);
      this.updateVisualSelection(checkedExtraRadio);
      this.updateDropdownHeader(checkedExtraRadio);
    }
  }

  syncAccordionsWithCurrentVariant() {
    if (!this.currentVariant?.title) return;

    const variantOptions = this.currentVariant.title.split(' / ').map(opt => opt.trim());

    variantOptions.forEach((optionValue, index) => {
      const matchingRadio = this.customizer.querySelector(`.variant-radio[data-option-index="${index}"][data-option-value="${optionValue}"]`);
      if (matchingRadio) {
        matchingRadio.checked = true;
        this.updateVisualSelection(matchingRadio);
        this.updateDropdownHeader(matchingRadio);

        const dropdownSection = matchingRadio.closest('[data-variant-dropdown]');
        if (dropdownSection) {
          const priceDiffSpan = dropdownSection.querySelector('[data-price-tag]');
          if (priceDiffSpan) {
            this.updatePriceDifference(matchingRadio, priceDiffSpan);
          }
        }
      }
    });
  }

  handleVariantRadioChange(radio, event) {
    super.handleVariantRadioChange(radio, event);

    if (radio.classList.contains('extra-variant-radio')) {
      this.updateExtraVariantSelection(radio);
    } else {
      this.updateSelectedVariant();
      const dropdownSection = radio.closest('[data-variant-dropdown]');
      if (dropdownSection) {
        const priceDiffSpan = dropdownSection.querySelector('[data-price-tag]');
        if (priceDiffSpan) {
          this.updatePriceDifference(radio, priceDiffSpan);
        }
      }
    }
  }

  /**
   * Overrides the base class's click handler for options to ensure
   * dropdown progression happens even when re-selecting an already chosen variant.
   */
  handleOptionClick(event) {
    const triggerRadioChange = (radio) => {
      if (radio) {
        const wasChecked = radio.checked;
        radio.checked = true;

        // If the radio was already checked, the 'change' event won't fire natively.
        // We must call the handler directly to trigger the dropdown progression.
        // If it wasn't checked, dispatching the 'change' event is sufficient and safer.
        if (wasChecked) {
          this.handleVariantRadioChange(radio, event);
        } else {
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    };

    // Handle clicks on option containers
    const moduleOption = event.target.closest('.module-option');
    if (moduleOption && !event.target.matches('input[type="radio"]')) {
      const radio = moduleOption.querySelector('.variant-radio, .extra-variant-radio');
      triggerRadioChange(radio);
      return;
    }

    // Handle list option clicks
    const listOption = event.target.closest('.module-list-option');
    if (listOption) {
      const radio = listOption.querySelector('.variant-radio');
      triggerRadioChange(radio);
      return;
    }
  }

  updateSelectedVariant() {
    const selectedOptions = [];
    const accordionSections = this.customizer.querySelectorAll('[data-variant-dropdown]');

    accordionSections.forEach((section) => {
      const selectedRadio = section.querySelector('.variant-radio:checked');
      if (selectedRadio && selectedRadio.dataset.optionIndex && selectedRadio.dataset.optionValue) {
        selectedOptions[selectedRadio.dataset.optionIndex] = selectedRadio.dataset.optionValue;
      }
    });

    const matchingVariant = this.findVariantByOptions(selectedOptions);

    if (matchingVariant) {
      if (!this.currentVariant || this.currentVariant.id !== matchingVariant.id) {
        this.currentVariant = matchingVariant;
        this.updateFormVariantId(matchingVariant.id);
        this.updateURL(matchingVariant.id);
      }
      this.updatePrice();
    }
  }

  findVariantByOptions(selectedOptions) {
    if (!this.variants || selectedOptions.length === 0) return null;

    return this.variants.find(variant => {
      const variantOptions = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
      return selectedOptions.every((selectedOption, index) => {
        if (!selectedOption) return true;
        return variantOptions[index] === selectedOption;
      });
    });
  }

  updateExtraVariantSelection(radio) {
    if (radio.checked && radio.dataset.extraVariantId) {
      const extraVariant = {
        id: radio.dataset.extraVariantId,
        label: radio.dataset.label,
        price: parseFloat(radio.dataset.price || 0)
      };

      this.extraVariants.set('extra-variant', extraVariant);
      this.setStorageItem(this.storageKey, radio.value);
    }
  }

  updatePriceDifference(radio, priceDiffElement) {
    if (!radio.dataset.price || !priceDiffElement) return;

    const selectedPrice = parseInt(radio.dataset.price, 10);
    if (isNaN(selectedPrice)) return;

    const firstVariant = this.variants && this.variants.length > 0 ? this.variants[0] : null;
    if (!firstVariant) return;

    const priceDiff = selectedPrice - firstVariant.price;
    priceDiffElement.style.display = 'inline';

    if (priceDiff !== 0) {
      priceDiffElement.textContent = `${priceDiff > 0 ? '+' : ''}${this.formatMoney(priceDiff)}`;
      priceDiffElement.classList.add('has-price');
    } else {
      priceDiffElement.textContent = `+${this.formatMoney(0)}`;
      priceDiffElement.classList.remove('has-price');
    }
  }

  updatePrice() {
    if (!this.currentVariant?.price || !this.priceContainer) return;

    const priceAmount = this.currentVariant.price;
    const comparePrice = this.currentVariant.compare_at_price;

    // Only update if this is the actual price container, not a nested one
    if (this.priceContainer.hasAttribute('ref') && this.priceContainer.getAttribute('ref') === 'priceContainer') {
      // We're targeting the inner price container directly
      this.priceContainer.innerHTML = this.renderPriceDisplayInner(priceAmount, comparePrice);
    } else {
      // We're targeting the outer container
      this.priceContainer.innerHTML = this.renderPriceDisplay(priceAmount, comparePrice);
    }

    // Update Sticky Price if exists
    if (!this.stickyPriceContainer) {
      this.stickyPriceContainer = document.querySelector('.sticky-add-to-cart__price') ||
        document.querySelector('[data-testid="sticky-price-display"]');
    }

    if (this.stickyPriceContainer) {
      this.stickyPriceContainer.innerHTML = this.renderPriceDisplay(priceAmount, comparePrice);
    }
  }

  updateURL(variantId) {
    if (!variantId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  onFormSubmit(event) {
    if (this.currentVariant) {
      this.updateFormVariantId(this.currentVariant.id);
      this.handleCustomAddToCart(event);
      return false;
    }
    return true;
  }

  onAddToCart(event) {
    if (this.currentVariant) {
      this.updateFormVariantId(this.currentVariant.id);
      this.handleCustomAddToCart(event);
      return false;
    }
    return true;
  }

  async handleCustomAddToCart(event) {
    if (!this.currentVariant) {
      this.showMessage('Lütfen bir varyant seçin', 'error');
      return;
    }

    const quantity = this.quantityInput ? parseInt(this.quantityInput.value) : 1;
    const properties = {};

    // Add variant option values with labels
    if (this.currentVariant.title && this.optionNames) {
      const optionValues = this.currentVariant.title.split(' / ').map(v => v.trim());
      this.optionNames.forEach((optionName, index) => {
        if (optionValues[index]) {
          properties[optionName] = optionValues[index];
        }
      });
    }

    // Add extra variants (Kumaş seçimi)
    this.extraVariants.forEach((extraVariant) => {
      properties['Kumaş seçimi'] = extraVariant.label;
    });

    const cartItem = {
      id: this.currentVariant.id,
      quantity: quantity,
      properties: properties
    };

    try {
      await this.addToCart(cartItem);
      this.showMessage('Ürün sepete eklendi', 'success');
    } catch (error) {
      this.showMessage(error.message, 'error');
    }
  }

  /**
   * Override base method to ensure ALL inputs with name="id" are updated.
   * This includes inputs inside the form and inputs linked via 'form' attribute.
   */
  updateFormVariantId(variantId) {
    if (!this.productForm || !variantId) return;

    // Update cached input
    if (this.variantIdInput) {
      this.variantIdInput.value = variantId;
    }

    // 1. Find inputs inside the form
    const internalInputs = Array.from(this.productForm.querySelectorAll('input[name="id"]'));

    // 2. Find inputs outside the form linked by form attribute
    let externalInputs = [];
    if (this.productForm.id) {
      externalInputs = Array.from(document.querySelectorAll(`input[name="id"][form="${this.productForm.id}"]`));
    }

    const allInputs = [...internalInputs, ...externalInputs];

    if (allInputs.length === 0) {
      console.warn('SingleProductCustomizer: No inputs with name="id" found for variant update.');
    }

    allInputs.forEach(input => {
      // Avoid infinite loops if we are triggered by an input change
      if (input.value !== variantId.toString()) {
        input.value = variantId;

        // Trigger events for theme compatibility
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Also try to update value of any select element with name="id" (unlikely but possible)
    const selects = this.productForm.querySelectorAll('select[name="id"]');
    selects.forEach(select => {
      if (select.value !== variantId.toString()) {
        select.value = variantId;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  saveState() {
    if (this.extraVariants.has('extra-variant')) {
      const extraVariant = this.extraVariants.get('extra-variant');
      this.setStorageItem(this.storageKey, extraVariant.id);
    }
  }
}

// Initialize
const initializeSingleCustomizer = function () {
  try {
    const customizerElements = document.querySelectorAll('[data-single-customizer]');
    customizerElements.forEach(customizer => {
      if (!customizer._customizerInstance) {
        new SingleProductCustomizer(customizer);
      }
    });
  } catch (error) {
    console.error('Error initializing Single Customizer:', error);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSingleCustomizer);
} else {
  initializeSingleCustomizer();
}

document.addEventListener('shopify:section:load', initializeSingleCustomizer);
document.addEventListener('shopify:section:select', initializeSingleCustomizer);
document.addEventListener('shopify:block:select', initializeSingleCustomizer); 